/**
 * Context-Aware Label Configuration
 *
 * Provides display-time label transformation for icons that appear in
 * multiple attack surface categories (Internet vs Intranet).
 *
 * This allows users to distinguish "External Server" from "Internal Server"
 * during icon selection without changing stored icon IDs.
 *
 * @example
 * ```ts
 * // In IconCard component:
 * const displayLabel = getContextualLabel(icon, categoryContext);
 * // Returns "External Server" when categoryContext is "internet"
 * // Returns "Internal Server" when categoryContext is "intranet"
 * // Returns "Server" for other categories (no prefix)
 * ```
 */

import type { CategoryId } from './categories.config';

/**
 * Icon IDs that appear in multiple categories and need context prefixes.
 * These are shared between Internet and Intranet categories.
 */
export const SHARED_ICON_IDS: readonly string[] = [
  'infrastructure:Server',
  'infrastructure:Computer',
  'infrastructure:Database',
] as const;

/**
 * Context prefixes to apply for each category.
 * Only categories with shared icons need prefixes.
 */
export const CONTEXT_PREFIXES: Partial<Record<CategoryId, string>> = {
  internet: 'External',
  intranet: 'Internal',
};

/**
 * Icon data needed for contextual label generation.
 * Matches the subset of IconDefinition we need.
 */
interface IconLabelData {
  id: string;
  label: string;
}

/**
 * Get the contextual label for an icon based on the current category.
 *
 * For shared icons (Server, Computer, Database) in internet/intranet categories,
 * adds appropriate prefix (External/Internal). For all other cases, returns
 * the original label unchanged.
 *
 * @param icon - Icon with id and label properties
 * @param categoryId - Current category context from IconPicker
 * @returns Label with context prefix if applicable, otherwise original label
 *
 * @example
 * ```ts
 * // Shared icon in internet category
 * getContextualLabel({ id: 'infrastructure:Server', label: 'Server' }, 'internet')
 * // Returns: 'External Server'
 *
 * // Shared icon in intranet category
 * getContextualLabel({ id: 'infrastructure:Server', label: 'Server' }, 'intranet')
 * // Returns: 'Internal Server'
 *
 * // Non-shared icon (no prefix added)
 * getContextualLabel({ id: 'aws:Lambda', label: 'Lambda' }, 'cloud')
 * // Returns: 'Lambda'
 *
 * // Shared icon in category without prefix (e.g., "all")
 * getContextualLabel({ id: 'infrastructure:Server', label: 'Server' }, 'all')
 * // Returns: 'Server'
 * ```
 */
export function getContextualLabel(
  icon: IconLabelData,
  categoryId: CategoryId | string
): string {
  const prefix = CONTEXT_PREFIXES[categoryId as CategoryId];

  // No prefix defined for this category
  if (!prefix) {
    return icon.label;
  }

  // Only add prefix for shared icons
  if (!SHARED_ICON_IDS.includes(icon.id)) {
    return icon.label;
  }

  return `${prefix} ${icon.label}`;
}
